import { memo, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { previewSimAction, useSimAction } from "@/hooks/useSims";
import { shortIccid, type ActionResponse } from "@/lib/sims";

export type SimActionTarget =
  | { kind: "sims"; simIds: string[] }
  | { kind: "device"; instanceId: string; deviceName: string };

interface SimActionDialogProps {
  action: "activate" | "deactivate" | null;
  target: SimActionTarget | null;
  onOpenChange: (open: boolean) => void;
  onDone?: () => void;
}

/**
 * Confirm dialog for activate/deactivate. Loads a server-side dry-run preview
 * first so the user sees exactly which SIMs are skipped and why.
 */
export const SimActionDialog = memo(function SimActionDialog({
  action,
  target,
  onOpenChange,
  onDone,
}: SimActionDialogProps) {
  const open = !!action && !!target;
  const [preview, setPreview] = useState<ActionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const runAction = useSimAction();
  // Callers may pass a new target object on every render; key on content.
  const targetKey = target ? JSON.stringify(target) : null;

  useEffect(() => {
    if (!open || !action || !target) return;
    let cancelled = false;
    setPreview(null);
    setError(null);
    previewSimAction(
      target.kind === "device"
        ? { instanceId: target.instanceId, action }
        : { simIds: target.simIds, action },
    )
      .then((result) => !cancelled && setPreview(result))
      .catch((err: Error) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, action, targetKey]);

  const queued = preview?.preview.filter((p) => p.outcome === "queued") ?? [];
  const skipped = preview?.preview.filter((p) => p.outcome === "skipped") ?? [];
  const verb = action === "activate" ? "Activate" : "Deactivate";

  const confirm = () => {
    if (!action || !target) return;
    runAction.mutate(
      target.kind === "device"
        ? { instanceId: target.instanceId, action }
        : { simIds: target.simIds, action },
      {
        onSuccess: () => {
          onOpenChange(false);
          onDone?.();
        },
      },
    );
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="sim-action-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {verb}{" "}
            {target?.kind === "device"
              ? `all SIMs of ${target.deviceName}`
              : "selected SIMs"}
            ?
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              {!preview && !error && (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Checking SIMs…
                </span>
              )}
              {error && <span className="text-destructive">{error}</span>}
              {preview && (
                <>
                  <p data-testid="sim-action-summary">
                    {queued.length} SIM{queued.length === 1 ? "" : "s"} will be{" "}
                    {action === "activate" ? "activated" : "deactivated"}
                    {skipped.length > 0 && `, ${skipped.length} skipped`}. The
                    carrier usually confirms within a minute.
                  </p>
                  {skipped.length > 0 && (
                    <div>
                      <p className="font-medium text-foreground">Skipped</p>
                      <ul
                        className="mt-1 max-h-48 space-y-1 overflow-y-auto"
                        data-testid="sim-action-skipped"
                      >
                        {skipped.map((entry) => (
                          <li key={entry.simId} className="flex gap-2">
                            <span className="font-mono">
                              {shortIccid(entry.iccid)}
                            </span>
                            <span>{entry.reason}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-sim-action"
            disabled={!preview || queued.length === 0 || runAction.isPending}
            onClick={(event) => {
              event.preventDefault();
              confirm();
            }}
          >
            {runAction.isPending ? "Queuing…" : `${verb} ${queued.length}`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
});
